<template>
  <div class="privacy-policy-page">
    <!-- Header Component -->
    <Header />

    <div class="privacy-policy">
      <div class="main-container">
        <!-- Header Section -->

        <div class="title-container">
          <div class="corner corner-tl"></div>
          <div class="corner corner-tr"></div>
          <div class="corner corner-bl"></div>
          <div class="corner corner-br"></div>
          <h1>Privacy Policy</h1>
        </div>

        <!-- Introduction Section -->
        <div class="section-container">
          <section class="policy-section intro-section">
            <div class="section-header">
              <div class="icon"><EyeIcon /></div>
              <h2>Introduction</h2>
            </div>
            <p>
              ElevatED ("we," "our," or "us") is committed to protecting your privacy and ensuring
              the security of your personal information. This Data Privacy Policy explains how we
              collect, use, store, and protect your personal data in compliance with the Republic
              Act No. 10173, also known as the Data Privacy Act of 2012 of the Philippines.
            </p>
            <p>
              By using our web-based tutoring platform, you consent to the collection and use of
              your personal information as described in this policy.
            </p>
            <div class="info-box">
              <p><strong>Last Updated:</strong> December 2025</p>
              <p><strong>Effective Date:</strong> December 2025</p>
            </div>
          </section>
        </div>

        <!-- Information We Collect Section -->
        <div class="section-container">
          <section class="policy-section info-collect-section">
            <div class="section-header">
              <div class="icon"><CircleStackIcon /></div>
              <h2>Information We Collect</h2>
            </div>

            <div class="subsection">
              <h3>Personal Information</h3>
              <ul class="info-list">
                <li>
                  <strong>Account Information:</strong> Full name, email address, password
                  (encrypted), phone number, date of birth
                </li>
                <li>
                  <strong>Student Verification:</strong> School ID photo, selfie with school ID,
                  school name, grade level, student number
                </li>
                <li>
                  <strong>Tutor Credentials:</strong> Transcript of Records (TOR), certificates,
                  diploma, curriculum vitae, clearances, signed contracts, optional introductory
                  videos
                </li>
                <li>
                  <strong>Academic Information:</strong> Subject preferences, tutoring history,
                  session notes, feedback and ratings
                </li>
              </ul>
            </div>
            <hr class="section-divider" />

            <div class="subsection technical-info">
              <h3>Technical Information</h3>
              <ul class="info-list">
                <li>
                  <strong>Usage Data:</strong> Login times, session duration, features used,
                  platform interactions
                </li>
                <li>
                  <strong>Device Information:</strong> IP address, browser type, operating system,
                  device identifiers
                </li>
                <li>
                  <strong>Communication Data:</strong> Messages between users, session recordings
                  (if applicable), uploaded files
                </li>
              </ul>
            </div>
          </section>
        </div>

        <!-- How We Use Your Information Section -->
        <div class="section-container">
          <section class="policy-section use-info-section">
            <div class="section-header">
              <div class="icon"><UsersIcon /></div>
              <h2>How We Use Your Information</h2>
            </div>

            <div class="two-column">
              <div class="column">
                <h3>Platform Operations</h3>
                <ul>
                  <li>Account creation and authentication</li>
                  <li>Tutor-student matching and recommendations</li>
                  <li>Session scheduling and management</li>
                  <li>AI-powered study material generation</li>
                  <li>Quality assurance and feedback systems</li>
                </ul>
              </div>
              <div class="column">
                <h3>Safety & Compliance</h3>
                <ul>
                  <li>Identity verification and fraud prevention</li>
                  <li>Tutor credential validation</li>
                  <li>Platform security and monitoring</li>
                  <li>Legal compliance and reporting</li>
                  <li>Dispute resolution and support</li>
                </ul>
              </div>
            </div>
          </section>
        </div>

        <!-- Third-Party Integrations Section -->
        <div class="section-container">
          <section class="policy-section third-party-section">
            <div class="section-header">
              <div class="icon"><LockClosedIcon /></div>
              <h2>Third-Party Integrations</h2>
            </div>
            <p>
              ElevatED integrates with the following third-party services to enhance platform
              functionality:
            </p>

            <div class="three-column">
              <div class="column">
                <h3>Google Services</h3>
                <ul>
                  <li>Google Calendar API (scheduling)</li>
                  <li>Google Gemini (AI processing)</li>
                  <li>Gmail (email notifications)</li>
                  <li>Firestore Database</li>
                </ul>
              </div>
              <div class="column">
                <h3>Hugging Face</h3>
                <ul>
                  <li>Natural Language Processing</li>
                  <li>Text analysis and generation</li>
                  <li>AI model hosting</li>
                </ul>
              </div>
              <div class="column">
                <h3>Email & Messaging Services</h3>
                <ul>
                  <li>OTP verification</li>
                  <li>Account notifications</li>
                  <li>System updates</li>
                  <li>CometChat API for messaging</li>
                </ul>
              </div>
            </div>

            <div class="warning-box">
              <p>
                <strong>Note:</strong> These third-party services have their own privacy policies.
                We encourage you to review their policies to understand how they handle your data.
              </p>
            </div>
          </section>
        </div>

        <!-- Data Protection & Security Section -->
        <div class="section-container">
          <section class="policy-section security-section">
            <div class="section-header">
              <div class="icon"><ShieldCheckIcon /></div>
              <h2>Data Protection & Security</h2>
            </div>

            <div class="two-column">
              <div class="column">
                <h3>Security Measures</h3>
                <ul>
                  <li>End-to-end encryption for sensitive data</li>
                  <li>Secure password hashing and storage</li>
                  <li>Regular security audits and updates</li>
                  <li>Role-based access control (RBAC)</li>
                  <li>Secure file upload and storage</li>
                  <li>Two-factor authentication options</li>
                </ul>
              </div>
              <div class="column">
                <h3>Data Retention</h3>
                <ul>
                  <li>Account data: Retained while account is active</li>
                  <li>Session records: 2 years for academic tracking</li>
                  <li>Tutor credentials: 5 years for verification</li>
                  <li>Communication logs: 1 year for support</li>
                  <li>Deleted upon account closure</li>
                </ul>
              </div>
            </div>
          </section>
        </div>

        <!-- Your Data Rights Section -->
        <div class="section-container">
          <section class="policy-section rights-section">
            <div class="section-header">
              <div class="icon"><UsersIcon /></div>
              <h2>Your Data Rights</h2>
            </div>
            <p>
              Under the Data Privacy Act of 2012, you have the following rights regarding your
              personal data:
            </p>

            <div class="rights-grid">
              <div class="right-item">
                <div class="right-icon">
                  <EyeIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Access</h3>
                  <p>Request copies of your personal data</p>
                </div>
              </div>
              <div class="right-item">
                <div class="right-icon">
                  <UserIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Rectification</h3>
                  <p>Correct inaccurate or incomplete data</p>
                </div>
              </div>
              <div class="right-item">
                <div class="right-icon">
                  <TrashIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Erasure</h3>
                  <p>Request deletion of your personal data</p>
                </div>
              </div>
              <div class="right-item">
                <div class="right-icon">
                  <ArrowPathIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Portability</h3>
                  <p>Transfer your data to another service</p>
                </div>
              </div>
              <div class="right-item">
                <div class="right-icon">
                  <ExclamationTriangleIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Object</h3>
                  <p>Object to certain data processing</p>
                </div>
              </div>
              <div class="right-item">
                <div class="right-icon">
                  <NoSymbolIcon />
                </div>
                <div class="right-content">
                  <h3>Right to Restrict</h3>
                  <p>Limit how we process your data</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Contact Us Section -->
        <div class="section-container">
          <section class="policy-section contact-section">
            <div class="section-header contact-header">
              <span class="contact-header-icon" aria-hidden="true"><EnvelopeIcon /></span>
              <h2>Contact Us</h2>
            </div>

            <p class="contact-intro">
              If you have any questions about this Privacy Policy or wish to exercise your data
              rights, please reach out to us:
            </p>

            <div class="contact-cards">
              <div class="contact-card">
                <div class="leading-icon email" aria-hidden="true"><EnvelopeIcon /></div>
                <div class="card-content">
                  <h3>Email</h3>
                  <p>elevated.tutorapp@gmail.com</p>
                </div>
              </div>

              <div class="contact-card">
                <div class="leading-icon address" aria-hidden="true"><MapPinIcon /></div>
                <div class="card-content">
                  <h3>Address</h3>
                  <p>Sampaloc, Manila, Philippines</p>
                </div>
              </div>
            </div>

            <div class="response-time-box">
              <p>
                <span class="response-label">Response Time:</span> We will respond to your
                privacy-related inquiries within 30 days as required by the Data Privacy Act of
                2012.
              </p>
            </div>
          </section>
        </div>

        <!-- Policy Updates Section (no icon per mock) -->
        <div class="section-container">
          <section class="policy-section">
            <div class="section-header">
              <h2>Policy Updates</h2>
            </div>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our
              practices or legal requirements. We will notify you of any material changes by:
            </p>
            <ul>
              <li>Posting the updated policy on our platform</li>
              <li>Sending email notifications to registered users</li>
              <li>Displaying prominent notices on the platform</li>
            </ul>
            <p>
              Your continued use of ElevatED after any changes constitutes your acceptance of the
              updated Privacy Policy.
            </p>
          </section>
        </div>
      </div>
    </div>

    <!-- Footer Component -->
    <Footer />
  </div>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import Header from "./Header.vue";
import Footer from "./Footer.vue";
import {
  EyeIcon,
  CircleStackIcon,
  UsersIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  UserIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from "@heroicons/vue/24/outline";

const router = useRouter();

const goBack = () => {
  router.go(-1);
};

const acceptPolicy = () => {
  localStorage.setItem("privacyPolicyAccepted", new Date().toISOString());
  router.push("/");
};
</script>

<style scoped>
.privacy-policy-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.privacy-policy {
  flex: 1;
  background: transparent;
  padding: 3rem 1rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  margin-top: 150px; /* Increased margin to account for fixed header */
}

.main-container {
  max-width: 760px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
}

/* Title Container with Square Border */
.title-container {
  display: block;
  position: relative;
  padding: 1rem 2rem;
  width: 100%;
  margin: 0 auto 3rem auto;
  text-align: center;
}

/* Square border */
.title-container::before {
  content: "";
  position: absolute;
  top: -12px;
  left: 0;
  right: 0;
  bottom: -12px;
  border: 3px solid #5271ff;
  border-radius: 0;
  background: transparent;
}

/* Corner squares */
.corner {
  position: absolute;
  width: 14px;
  height: 14px;
  background: #5271ff;
}

.corner-tl {
  top: -18px;
  left: -6px;
}
.corner-tr {
  top: -18px;
  right: -6px;
}
.corner-bl {
  bottom: -18px;
  left: -6px;
}
.corner-br {
  bottom: -18px;
  right: -6px;
}

.title-container h1 {
  color: #5271ff;
  font-size: 3.25rem;
  font-weight: 700;
  margin: 0;
  position: relative;
  z-index: 1;
  white-space: nowrap;
}

/* Policy Sections */
.section-container {
  margin: 1.25rem 0;
}

.policy-section {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12), 0 4px 10px rgba(0, 0, 0, 0.06);
}

.intro-section {
  background: #ffffff;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0;
}

.section-header .icon {
  margin-right: 0.5rem;
}

.section-header h2 {
  flex: 1;
  margin: 0;
}

.icon {
  width: 24px;
  height: 24px;
  background: transparent;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Removed image-based icons to prevent overlap; using SVG Heroicons instead */

/* Heroicons inside generic section icon box */
.icon svg {
  width: 20px;
  height: 20px;
  color: #94a3b8;
}

.intro-section .section-header .icon svg {
  color: #5271ff;
}
.info-collect-section .section-header .icon svg {
  color: #22c55e;
} /* green database */
.use-info-section .section-header .icon svg {
  color: #8b5cf6;
} /* purple users */
.third-party-section .section-header .icon svg {
  color: #f59e0b;
} /* orange lock */
.security-section .section-header .icon svg {
  color: #ef4444;
} /* red shield */
.rights-section .section-header .icon svg {
  color: #8b5cf6;
} /* purple users */

h2 {
  color: #2d3748;
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
}

h3 {
  color: #4a5568;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  margin-top: 0;
}

p {
  color: #6b7280;
  line-height: 1.3;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: 400;
}

/* Lists */
ul {
  margin: 1rem 0;
  padding-left: 0;
  list-style: none;
}

li {
  color: #4a5568;
  margin-bottom: 0.8rem;
  padding-left: 1.5rem;
  position: relative;
  font-size: 0.9rem;
  line-height: 1.6;
  font-weight: 400;
}

.policy-section ul:not(.info-list) li::before {
  content: "•";
  color: #111827;
  font-weight: 400;
  position: absolute;
  left: 0;
  font-size: 1.2em;
  line-height: 0.8;
}

/* Compact, colored bullets for Information We Collect */
.info-list li::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 0.65em;
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 9999px;
}

/* Optional alternate color for the second subsection list */
.technical-info .info-list li::before {
  background: #3b82f6;
}

/* Technical Information styling */
.technical-info h3 {
  color: #000000;
}

.technical-info li {
  color: #000000;
}

.technical-info strong {
  color: #000000;
}

strong {
  font-weight: 700;
  color: #2d3748;
}

/* Info Box */
.info-box {
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1.5rem;
  position: relative;
}

.info-box::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #5271ff;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

.info-box p,
.info-box strong {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #2b6cb0;
}

/* Warning Box */
.third-party-section .warning-box {
  background: #fff7e6;
  border-radius: 12px;
  padding: 1rem 1.5rem;
  margin-top: 1.5rem;
  position: relative;
  overflow: hidden;
}

.third-party-section .warning-box::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: #f59e0b;
}

.third-party-section .warning-box p {
  color: #8b5d33;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.third-party-section .warning-box strong {
  color: #8b5d33;
  font-weight: 600;
  margin-right: 0.5rem;
}

/* Section visual divider */
.section-divider {
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
}

/* --- FIXED GRID SECTIONS --- */
.two-column {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 1rem;
}

/* How We Use Your Information: column titles in black */
.use-info-section h3 {
  color: #000000;
}

.three-column {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 1.5rem 0;
}

.third-party-section .column {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.third-party-section .column h3 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.third-party-section .column ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.third-party-section .column li {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 0.35rem;
  padding-left: 1rem;
  position: relative;
}

.third-party-section .column li:last-child {
  margin-bottom: 0;
}

.third-party-section .column li::before {
  content: "•";
  color: #9ca3af;
  position: absolute;
  left: 0;
  top: -1px;
}

/* Override note style to match mock (full border, soft yellow) */
.third-party-section .warning-box {
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-left: none;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}

.third-party-section .warning-box p,
.third-party-section .warning-box strong {
  color: #92400e;
}

/* (Removed duplicate .contact-cards definition; see section near Contact Us for the active styles) */

.rights-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.right-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.right-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: #eef2ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.right-icon svg {
  width: 1.25rem;
  height: 1.25rem;
  color: #6366f1;
}

.right-content {
  flex: 1;
}

.right-content h3 {
  color: #111827;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.right-content p {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
}

/* Contact Us Section */
.contact-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 1.5rem;
  padding: 2rem;
  text-align: center;
}

.contact-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.contact-header-icon {
  width: 28px;
  height: 28px;
  color: #22c55e; /* green envelope like mock */
  display: inline-flex;
}

.contact-header-icon svg {
  width: 28px;
  height: 28px;
}

.contact-intro {
  color: #4a5568;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.contact-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 1rem 0 1.5rem 0;
}

.contact-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.6rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

/* Leading icons inside contact cards */
.leading-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  background: transparent;
}

.leading-icon svg {
  width: 16px;
  height: 16px;
}

.leading-icon.email {
  color: #2563eb;
  background: transparent;
} /* blue */
.leading-icon.phone {
  color: #16a34a;
  background: transparent;
} /* green */
.leading-icon.address {
  color: #ef4444;
  background: transparent;
} /* red */

.card-icon img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.card-content h3 {
  color: #1a202c;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.card-content p {
  color: #4a5568;
  font-size: 0.85rem;
  line-height: 1.2;
}

.response-time-box {
  background: #eef2f6;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 1rem;
  margin-top: 1.5rem;
}

.response-label {
  font-weight: 700;
  color: #1a202c;
  font-size: 0.9rem;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .privacy-policy {
    padding: 1rem;
    margin-top: 120px; /* Adjusted margin for mobile view */
  }

  .main-container {
    padding: 1.5rem;
  }

  .title-container h1 {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .content-container h2 {
    font-size: 1.25rem;
  }

  .button-container {
    flex-direction: column;
  }

  .back-button,
  .accept-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .privacy-policy {
    margin-top: 100px; /* Further adjusted margin for smaller devices */
  }
}

#app {
  display: flex;
  flex-direction: column;
  padding: 0;
  max-width: 100vw;
  margin: 0;
  width: 100%;
}
</style>
